/**
 * @file api-customer-portal.ts
 * @description Admin-API клиента для Customer Portal endpoints (phase10.6):
 *   - POST /admin/customers/:id/invite — менеджер шлёт инвайт
 *   - GET  /admin/customers/:id/account-status — статус ЛК клиента
 *   - POST /admin/orders/:id/request-approval — отправить Deal клиенту
 *   - GET  /admin/orders/:id/approval-status — статус последнего approval
 */

import { apiFetch } from "./api-client";

// ─── Account status ──────────────────────────────────────────────────────────

export interface CustomerInvitationDto {
  source: string;
  expiresAt: string;
  consumedAt: string | null;
  createdAt: string;
  /** pending — ссылка ещё действует; activated / superseded / expired — нет. */
  status?: "pending" | "activated" | "superseded" | "expired";
  /**
   * OQ-70: ссылка действующего инвайта (та же, что в письме) — сервер хранит
   * токен зашифрованным и отдаёт её менеджеру с SHOP_MANAGE_CUSTOMERS, пока
   * инвайт не истёк; null — инвайт старый (до OQ-70) или уже не действует.
   */
  inviteUrl?: string | null;
}

export interface CustomerAccountStatusDto {
  activated: boolean;
  lastLoginAt: string | null;
  invitedAt: string | null;
  invitations: CustomerInvitationDto[];
}

export interface InviteResultDto {
  invitationId: string;
  expiresAt: string;
  /**
   * Ссылка активации ЛК — та же, что ушла в письме. Сервер отдаёт её один
   * раз (в базе только хэш токена): показать менеджеру, чтобы он мог
   * скопировать и отправить руками, если письмо не дошло (2026-09-22).
   */
  inviteUrl: string;
}

export const customerPortal = {
  getAccountStatus: (customerId: string) =>
    apiFetch<CustomerAccountStatusDto>(
      `/admin/customers/${customerId}/account-status`,
    ),

  sendInvite: (customerId: string) =>
    apiFetch<InviteResultDto>(`/admin/customers/${customerId}/invite`, {
      method: "POST",
    }),
};

// ─── Approval ────────────────────────────────────────────────────────────────

export type DealApprovalStatusValue =
  | "PENDING"
  | "APPROVED"
  | "DECLINED"
  | "EXPIRED";

export type OrderApprovalStatusDto =
  | { exists: false }
  | {
      exists: true;
      status: DealApprovalStatusValue;
      expiresAt: string;
      decidedAt: string | null;
    };

export interface RequestApprovalResultDto {
  approvalId: string;
  expiresAt: string;
}

export const orderApprovals = {
  getStatus: (orderId: string) =>
    apiFetch<OrderApprovalStatusDto>(`/admin/orders/${orderId}/approval-status`),

  request: (orderId: string) =>
    apiFetch<RequestApprovalResultDto>(
      `/admin/orders/${orderId}/request-approval`,
      { method: "POST" },
    ),
};
